import './styles.css';
import packageMetadata from '../package.json';
import {
  DEFAULT_CONFIG,
  DEFAULT_VISIBILITY,
  LAB_PRESETS,
  LASER_BASE_PRESETS,
} from './config/defaults';
import { Vec3 } from './math/Vec3';
import { SceneRenderer } from './rendering/SceneRenderer';
import { ProjectionSequence } from './simulation/runSimulation';
import { SimulationClient } from './simulation/SimulationClient';
import { parseConfig } from './config/parseConfig';
import { compareMeasurements, measurementTemplate } from './simulation/measurements';
import type { SimulationConfig, SimulationSnapshot, ViewMode, VisibilityState } from './simulation/types';
import { APP_TEMPLATE } from './ui/templates';

const app = document.querySelector<HTMLDivElement>('#app');
if (!app) throw new Error('Application mount point was not found.');
app.innerHTML = APP_TEMPLATE;

function element<T extends HTMLElement>(selector: string): T {
  const result = document.querySelector<T>(selector);
  if (!result) throw new Error(`Missing UI element: ${selector}`);
  return result;
}

function numberValue(selector: string): number {
  return Number(element<HTMLInputElement>(selector).value);
}

function setText(selector: string, value: string): void {
  element(selector).textContent = value;
}

function formatInteger(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function formatMillimetres(value: number | null): string {
  if (value === null) return '—';
  const millimetres = value * 1000;
  return `${millimetres.toFixed(millimetres < 10 ? 2 : 1)} mm`;
}

function updateProjectionLaserControl(): void {
  const toggle = element<HTMLInputElement>('#show-projection-rays');
  const enabled = toggle.checked;
  setText('#projection-laser-state', enabled ? 'OCULTAR' : 'MOSTRAR');
  element<HTMLElement>('#projection-laser-control').classList.toggle('active', enabled);
}

function updateProjectionMeshControl(): void {
  const toggle = element<HTMLInputElement>('#show-projection-mesh');
  const enabled = toggle.checked;
  setText('#projection-mesh-state', enabled ? 'OCULTAR' : 'MOSTRAR');
  element<HTMLElement>('#projection-mesh-control').classList.toggle('active', enabled);
}

function updateProjectionAnimationControl(): void {
  const toggle = element<HTMLInputElement>('#animate-projection');
  const enabled = toggle.checked;
  setText('#projection-motion-state', enabled ? 'ACTIVO' : 'EN PAUSA');
  element<HTMLElement>('#projection-motion-control').classList.toggle('active', enabled);
}

function download(filename: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const anchor = document.createElement('a');
  anchor.href = URL.createObjectURL(blob);
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(anchor.href), 1000);
}

let currentConfig: SimulationConfig = { ...DEFAULT_CONFIG };
let visibility: VisibilityState = { ...DEFAULT_VISIBILITY };
let snapshot: SimulationSnapshot | null = null;
let projectionSequence: ProjectionSequence | null = null;
let displayedRays: SimulationSnapshot['rays'] = [];
let displayedStatistics: SimulationSnapshot['statistics'] | null = null;
let running = false;
const simulationClient = new SimulationClient();
let simulationRequest = 0;
let currentMode: ViewMode = 'TARGET';
let projectionFrameIndex = 0;
let lastProjectionFrameAt = 0;
let projectionAnimationStartedAt = 0;
let displayedRotation = new Vec3();
let displayedPivot = new Vec3();
let voxelTestRunning = false;
let voxelTestStartedAt = 0;
let lastVoxelTestRenderKey = '';
let animationWasEnabledBeforeTest = false;
let voxelTestEntries: Array<{
  voxelId: number;
  ray: SimulationSnapshot['rays'][number] | null;
}> = [];

const PROJECTION_FRAME_COUNT = 72;
const PROJECTION_FRAME_INTERVAL_MS = 100;

const viewport = element<HTMLElement>('#viewport');
const renderer = new SceneRenderer(viewport, visibility, (fps) => {
  setText('#stat-fps', `${fps.toFixed(0)} FPS`);
});
setText('#webgl-badge', renderer.getWebGlVersion().toUpperCase());
setText('#app-version', `v${packageMetadata.version}`);

function readConfig(): SimulationConfig {
  const read = (selector: string, previous: number, scale = 1): number => {
    const control = element<HTMLInputElement>(selector);
    return control.value === control.dataset.configValue ? previous : Number(control.value) * scale;
  };
  const cubeEdge = read('#cube-size', currentConfig.cubeSize.x, 0.01);
  return parseConfig({
    ...currentConfig,
    cubeSize: element<HTMLInputElement>('#cube-size').value === element<HTMLInputElement>('#cube-size').dataset.configValue
      ? currentConfig.cubeSize : new Vec3(cubeEdge, cubeEdge, cubeEdge),
    meshCount: numberValue('#mesh-count'),
    meshDensity: numberValue('#mesh-density'),
    minimumMeshDistance: numberValue('#mesh-separation'),
    filamentRadius: numberValue('#filament-radius'),
    meshPattern: element<HTMLSelectElement>('#projection-mesh-pattern').value as SimulationConfig['meshPattern'],
    meshStrategy: element<HTMLSelectElement>('#mesh-strategy').value as SimulationConfig['meshStrategy'],
    orientationRandomness: read('#orientation', currentConfig.orientationRandomness, Math.PI / 180),
    curvature: read('#curvature', currentConfig.curvature, 0.001),
    logicalLaserCount: numberValue('#projection-precision'),
    hemisphereRadius: read('#hemisphere-radius', currentConfig.hemisphereRadius, 0.01),
    laserMaxAngle: read('#laser-angle', currentConfig.laserMaxAngle, Math.PI / 180),
    occlusionEnabled: true,
    projectionFigure: element<HTMLSelectElement>('#projection-figure').value as SimulationConfig['projectionFigure'],
    projectionTolerance: numberValue('#projection-tolerance') / 1000,
  });
}

function writeLaserBasePreset(radius: number): void {
  const select = element<HTMLSelectElement>('#projection-laser-base');
  const customOption = select.querySelector<HTMLOptionElement>('option[value="CUSTOM"]');
  const matchingPreset = Object.entries(LASER_BASE_PRESETS).find(
    ([, preset]) => Math.abs(preset.radius - radius) < 1e-9,
  );
  select.value = matchingPreset?.[0] ?? 'CUSTOM';
  if (customOption) {
    customOption.textContent = `BASE · PERSONALIZADA · Ø${(radius * 200).toFixed(0)} CM`;
  }
}

function writeConfig(config: SimulationConfig): void {
  // Imported values need not exist among the public presets.
  for (const [selector, value] of [
    ['#mesh-separation', config.minimumMeshDistance], ['#filament-radius', config.filamentRadius],
    ['#laser-count', config.logicalLaserCount], ['#projection-precision', config.logicalLaserCount],
    ['#projection-mesh-pattern', config.meshPattern],
  ] as const) {
    const select = element<HTMLSelectElement>(selector);
    if (![...select.options].some(option => option.value === String(value))) {
      select.add(new Option(`Personalizado · ${value}`, String(value)));
    }
  }
  for (const [selector, value] of [
    ['#cube-size', config.cubeSize.x * 100], ['#orientation', config.orientationRandomness * 180 / Math.PI],
    ['#curvature', config.curvature * 1000], ['#hemisphere-radius', config.hemisphereRadius * 100],
    ['#laser-angle', config.laserMaxAngle * 180 / Math.PI],
  ] as const) {
    const input = element<HTMLInputElement>(selector);
    input.min = String(Math.min(Number(input.min), value));
    input.max = String(Math.max(Number(input.max), value));
    input.step = 'any';
  }
  element<HTMLInputElement>('#cube-size').value = String(config.cubeSize.x * 100);
  element<HTMLInputElement>('#mesh-count').value = String(config.meshCount);
  element<HTMLInputElement>('#mesh-density').value = String(config.meshDensity);
  element<HTMLSelectElement>('#mesh-separation').value = String(config.minimumMeshDistance);
  element<HTMLSelectElement>('#filament-radius').value = String(config.filamentRadius);
  element<HTMLSelectElement>('#mesh-pattern').value = config.meshPattern;
  element<HTMLSelectElement>('#projection-mesh-pattern').value = config.meshPattern;
  element<HTMLSelectElement>('#mesh-strategy').value = config.meshStrategy;
  element<HTMLInputElement>('#orientation').value = String((config.orientationRandomness * 180) / Math.PI);
  element<HTMLInputElement>('#curvature').value = String(config.curvature * 1000);
  element<HTMLSelectElement>('#laser-count').value = String(config.logicalLaserCount);
  element<HTMLSelectElement>('#projection-precision').value = String(config.logicalLaserCount);
  element<HTMLInputElement>('#hemisphere-radius').value = String(config.hemisphereRadius * 100);
  writeLaserBasePreset(config.hemisphereRadius);
  element<HTMLInputElement>('#laser-angle').value = String((config.laserMaxAngle * 180) / Math.PI);
  element<HTMLInputElement>('#occlusion').checked = true;
  element<HTMLSelectElement>('#projection-figure').value = config.projectionFigure;
  element<HTMLInputElement>('#projection-tolerance').value = String(config.projectionTolerance * 1000);
  for (const control of document.querySelectorAll<HTMLInputElement>('input[type="range"]')) {
    control.dataset.configValue = control.value;
  }
  updateOutputs();
}

function readVisibility(): VisibilityState {
  return {
    ...visibility,
    meshes: element<HTMLInputElement>('#show-meshes').checked,
    projectionMeshes: element<HTMLInputElement>('#show-projection-mesh').checked,
    lasers: element<HTMLInputElement>('#show-lasers').checked,
    projectionRays: element<HTMLInputElement>('#show-projection-rays').checked,
    desiredRays: element<HTMLInputElement>('#show-desired').checked,
    intersections: element<HTMLInputElement>('#show-intersections').checked,
    blockedRays: element<HTMLInputElement>('#show-blocked').checked,
  };
}

function updateOutputs(): void {
  setText('#cube-size-output', `${numberValue('#cube-size').toFixed(0)} cm`);
  setText('#orientation-output', `${numberValue('#orientation').toFixed(1)}°`);
  setText('#curvature-output', `${numberValue('#curvature').toFixed(1)} mm`);
  setText('#hemisphere-output', `${numberValue('#hemisphere-radius').toFixed(0)} cm`);
  setText('#angle-output', `${numberValue('#laser-angle').toFixed(0)}°`);
}

function updateProjectionControlOutputs(): void {
  setText('#pivot-x-output', `${numberValue('#pivot-x').toFixed(0)}%`);
  setText('#pivot-y-output', `${numberValue('#pivot-y').toFixed(0)}%`);
  setText('#pivot-z-output', `${numberValue('#pivot-z').toFixed(0)}%`);
  setText('#rotation-period-output', `${numberValue('#rotation-period').toFixed(1)} s`);
  setText('#voxel-test-speed-output', `${numberValue('#voxel-test-speed').toFixed(1)} vóxeles/s`);
  setText('#voxel-test-duration-output', `${numberValue('#voxel-test-duration').toFixed(0)} ms`);
}

function readRotationDirection(): Vec3 {
  return new Vec3(
    numberValue('#rotation-x-direction'),
    numberValue('#rotation-y-direction'),
    numberValue('#rotation-z-direction'),
  );
}

function readRotationPivot(): Vec3 {
  const cubeSize = snapshot?.config.cubeSize ?? currentConfig.cubeSize;
  return new Vec3(
    (numberValue('#pivot-x') / 100) * cubeSize.x * 0.5,
    (numberValue('#pivot-y') / 100) * cubeSize.y * 0.5,
    (numberValue('#pivot-z') / 100) * cubeSize.z * 0.5,
  );
}

function updateCoverage(result: SimulationSnapshot): void {
  const { coverage } = result;
  const coverageText = `${formatInteger(coverage.coveredVoxels)} / ${formatInteger(coverage.testedVoxels)} CUBIERTOS`;
  setText('#voxel-coverage-summary', coverageText);
  setText(
    '#voxel-coverage-result',
    coverage.complete
      ? `CATÁLOGO CALIBRADO 100% · ${coverageText}${coverage.addedLaserCount > 0 ? ` · +${coverage.addedLaserCount} LÁSERES` : ''}`
      : `${coverageText} · FALTAN ${coverage.missingVoxelIds.length}`,
  );
  element('#voxel-coverage-result').classList.toggle('incomplete', !coverage.complete);
}

function updateStatistics(result: SimulationSnapshot): void {
  const stats = result.statistics;
  const successPercent = stats.logicalRays > 0 ? (stats.successfulRays / stats.logicalRays) * 100 : 0;
  setText('#stat-success', formatInteger(stats.successfulRays));
  setText(
    '#stat-success-percent',
    `${successPercent.toFixed(1)}% of ${currentMode === 'TARGET' ? 'active fixed lasers' : 'fixed laser bank'}`,
  );
  setText(
    '#stat-meshes',
    result.diagnostics.generatedMeshes === result.diagnostics.requestedMeshes
      ? formatInteger(stats.meshCount)
      : `${formatInteger(stats.meshCount)} / ${formatInteger(result.diagnostics.requestedMeshes)}`,
  );
  setText('#stat-filaments', formatInteger(stats.filamentCount));
  setText('#stat-rays', formatInteger(stats.logicalRays));
  setText('#stat-blocked', formatInteger(stats.blockedRays));
  setText('#stat-occlusion', `${stats.occlusionPercentage.toFixed(1)}%`);
  setText('#stat-missed', formatInteger(stats.missedRays));
  setText('#stat-rejected', formatInteger(result.diagnostics.rejectedCandidates));
  setText(
    '#stat-clearance',
    result.diagnostics.minimumMeasuredDistance === null
      ? 'no near pairs'
      : `${(result.diagnostics.minimumMeasuredDistance * 1000).toFixed(3)} mm`,
  );
  setText('#stat-time', `${stats.simulationTimeMs.toFixed(1)} ms`);
  setText('#stat-figure', result.config.projectionFigure);
  setText('#stat-lightpoints', formatInteger(stats.successfulRays));
  setText('#stat-voxels', formatInteger(result.voxels.length));
  const quality = stats.projectionQuality;
  if (quality) {
    const coverage = quality.figureCoveragePercentage === null ? '—' : `${quality.figureCoveragePercentage.toFixed(1)}%`;
    setText('#quality-summary', `${coverage} de la figura`);
    setText('#quality-coverage', coverage);
    setText('#quality-unmatched', formatInteger(quality.unmatchedPoints));
    setText('#quality-rms', formatMillimetres(quality.mappingErrorRms));
    setText('#quality-p95', formatMillimetres(quality.mappingErrorP95));
    setText('#quality-max', formatMillimetres(quality.mappingErrorMax));
    setText('#quality-count', `${quality.activatedPoints} / ${quality.requestedPoints}`);
    setText('#quality-tolerance', formatMillimetres(quality.mappingTolerance));
  }
  setText(
    '#stat-requested-active',
    quality
      ? `${formatInteger(quality.requestedPoints)} / ${formatInteger(quality.activatedPoints)}`
      : '—',
  );
  setText('#stat-error-rms', formatMillimetres(quality?.mappingErrorRms ?? null));
  setText('#stat-error-p95', formatMillimetres(quality?.mappingErrorP95 ?? null));
  setText('#stat-error-max', formatMillimetres(quality?.mappingErrorMax ?? null));
  setText('#stat-mean-separation', formatMillimetres(quality?.meanActiveVoxelSeparation ?? null));
  setText('#seed-value', String(result.config.seed));
  element<HTMLElement>('#occlusion-bar').style.width = `${Math.min(100, stats.occlusionPercentage)}%`;
}

function setRunningState(isRunning: boolean, message: string): void {
  running = isRunning;
  element<HTMLButtonElement>('#run-simulation').disabled = isRunning;
  element('#run-indicator').classList.toggle('running', isRunning);
  setText('#simulation-status', message);
  setText('#public-status', message);
}

async function simulate(): Promise<void> {
  let config: SimulationConfig;
  try { config = readConfig(); } catch (error) {
    setText('#public-status', error instanceof Error ? error.message : 'Configuración inválida');
    return;
  }
  const request = ++simulationRequest;
  voxelTestRunning = false;
  element<HTMLButtonElement>('#voxel-test-toggle').textContent = 'INICIAR TEST';
  currentConfig = config;
  setRunningState(true, 'CALCULANDO…');
    try {
      const result = await simulationClient.run(config);
      if (request !== simulationRequest) return;
      snapshot = result;
      setText('#measurement-summary', 'Sin mediciones físicas importadas para este cálculo.');
      projectionSequence = new ProjectionSequence(snapshot);
      displayedRotation = new Vec3();
      displayedPivot = readRotationPivot();
      const initialFrame = projectionSequence.frame(displayedRotation, 0, displayedPivot);
      displayedRays = initialFrame.rays;
      displayedStatistics = initialFrame.statistics;
      projectionFrameIndex = 0;
      lastProjectionFrameAt = performance.now();
      projectionAnimationStartedAt = lastProjectionFrameAt;
      setText('#projection-frame', `FOTOGRAMA 01 / ${PROJECTION_FRAME_COUNT} · X 0° / Y 0° / Z 0°`);
      renderer.update(snapshot);
      renderer.resetCamera();
      renderer.updateProjectionFrame(
        initialFrame.rays,
        initialFrame.statistics.projectionQuality?.meanActiveVoxelSeparation ?? null,
      );
      updateCoverage(snapshot);
      setText(
        '#hardware-model',
        `${formatInteger(snapshot.emitters.length)} láseres · base Ø${(snapshot.config.hemisphereRadius * 200).toFixed(0)} cm · ${formatInteger(snapshot.voxels.length)} vóxeles · catálogo ${snapshot.coverage.complete ? 'calibrado' : 'incompleto'}`,
      );
      updateStatistics(
        currentMode === 'TARGET'
          ? { ...snapshot, rays: initialFrame.rays, statistics: initialFrame.statistics }
          : snapshot,
      );
      setRunningState(false, snapshot.coverage.complete ? 'CATÁLOGO CALIBRADO · Consulta la calidad de la figura' : 'CATÁLOGO INCOMPLETO');
    } catch (error) {
      if (request !== simulationRequest) return;
      console.error(error);
      setRunningState(false, error instanceof Error ? error.message : 'Error de simulación');
    }
}

function renderProjectionPose(rotation: Vec3, pivot: Vec3, frameIndex: number): void {
  if (!snapshot || !projectionSequence) return;
  const frame = projectionSequence.frame(rotation, frameIndex, pivot);
  displayedRotation = rotation;
  displayedPivot = pivot;
  displayedRays = frame.rays;
  displayedStatistics = frame.statistics;
  renderer.updateProjectionFrame(
    frame.rays,
    frame.statistics.projectionQuality?.meanActiveVoxelSeparation ?? null,
  );
  updateStatistics({ ...snapshot, rays: frame.rays, statistics: frame.statistics });
  setText(
    '#projection-frame',
    `FOTOGRAMA ${String(frameIndex + 1).padStart(2, '0')} / ${PROJECTION_FRAME_COUNT} · X ${Math.round((rotation.x * 180) / Math.PI)}° / Y ${Math.round((rotation.y * 180) / Math.PI)}° / Z ${Math.round((rotation.z * 180) / Math.PI)}°`,
  );
  setText(
    '#simulation-status',
    `SECUENCIA LÁSER · ${projectionSequence.activeLaserCount} ON / ${snapshot.emitters.length} FIJOS`,
  );
}

function finishVoxelTest(completed: boolean): void {
  if (!voxelTestRunning) return;
  voxelTestRunning = false;
  lastVoxelTestRenderKey = '';
  element<HTMLButtonElement>('#voxel-test-toggle').textContent = 'INICIAR TEST';
  if (completed) {
    setText('#voxel-test-current', 'TEST COMPLETADO · TODOS LOS VÓXELES RECORRIDOS');
    element<HTMLElement>('#voxel-test-progress-bar').style.width = '100%';
  } else {
    setText('#voxel-test-current', 'TEST DETENIDO');
  }
  element<HTMLInputElement>('#animate-projection').checked = animationWasEnabledBeforeTest;
  updateProjectionAnimationControl();
  projectionAnimationStartedAt = performance.now();
  renderProjectionPose(displayedRotation, displayedPivot, projectionFrameIndex);
}

function startVoxelTest(): void {
  if (!snapshot || running || snapshot.voxels.length === 0) return;
  if (voxelTestRunning) {
    finishVoxelTest(false);
    return;
  }
  voxelTestRunning = true;
  voxelTestStartedAt = performance.now();
  lastVoxelTestRenderKey = '';
  const rayByVoxelId = new Map(snapshot.rays.map((ray) => [ray.voxelId, ray]));
  voxelTestEntries = [...snapshot.voxels]
    .sort((first, second) => first.id - second.id)
    .map((voxel) => ({ voxelId: voxel.id, ray: rayByVoxelId.get(voxel.id) ?? null }));
  animationWasEnabledBeforeTest = element<HTMLInputElement>('#animate-projection').checked;
  element<HTMLInputElement>('#animate-projection').checked = false;
  updateProjectionAnimationControl();
  element<HTMLButtonElement>('#voxel-test-toggle').textContent = 'DETENER TEST';
  element<HTMLElement>('#voxel-test-progress-bar').style.width = '0%';
  const projectionRaysToggle = element<HTMLInputElement>('#show-projection-rays');
  if (!projectionRaysToggle.checked) {
    projectionRaysToggle.checked = true;
    visibility = readVisibility();
    renderer.setVisibility(visibility);
    updateProjectionLaserControl();
  }
  setText('#simulation-status', 'TEST SECUENCIAL DE VÓXELES');
}

function updateVoxelTest(timestamp: number): void {
  if (!voxelTestRunning || !snapshot) return;
  if (voxelTestEntries.length === 0) {
    finishVoxelTest(true);
    return;
  }
  const illuminationMs = numberValue('#voxel-test-duration');
  const requestedStepMs = 1000 / Math.max(0.1, numberValue('#voxel-test-speed'));
  const stepMs = Math.max(requestedStepMs, illuminationMs);
  const elapsed = timestamp - voxelTestStartedAt;
  const index = Math.floor(elapsed / stepMs);
  if (index >= voxelTestEntries.length) {
    finishVoxelTest(true);
    return;
  }
  const entry = voxelTestEntries[index];
  if (!entry) return;
  const ray = entry.ray;
  const illuminated = elapsed - index * stepMs < illuminationMs;
  const renderKey = `${index}:${illuminated}`;
  if (renderKey !== lastVoxelTestRenderKey) {
    renderer.updateProjectionFrame(illuminated && ray ? [ray] : []);
    lastVoxelTestRenderKey = renderKey;
  }
  const accessible = ray?.targetAccessible === true && ray.firstIntersection !== null;
  setText(
    '#voxel-test-current',
    `VÓXEL ${index + 1} / ${voxelTestEntries.length} · ${ray ? `LÁSER ${ray.emitterId}` : 'SIN LÁSER'} · ${accessible ? (illuminated ? 'ILUMINADO' : 'PAUSA') : 'NO ILUMINABLE'}`,
  );
  element<HTMLElement>('#voxel-test-progress-bar').style.width = `${((index + 1) / voxelTestEntries.length) * 100}%`;
  setText('#simulation-status', `TEST VÓXEL ${index + 1} / ${voxelTestEntries.length}`);
}

function selectMode(mode: ViewMode, future: boolean): void {
  currentMode = mode;
  document.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((button) => {
    button.classList.toggle('active', button.dataset.mode === mode);
  });
  renderer.setMode(mode);
  const titles: Record<ViewMode, [string, string, string]> = {
    STRUCTURE: ['01 / GEOMETRY', 'STRUCTURE FIELD', 'Physical filament network · dimensions in metres'],
    LASERS: ['02 / RAY CASTING', 'LASER PATHS', 'Actual first-hit paths through the volume'],
    OCCLUSION: ['04 / OCCLUSION', 'BLOCKED VS DESIRED', 'Orange stops at the first unintended filament'],
    TARGET: ['HOLO—NET', 'PROYECCIÓN LÁSER 3D', 'Puntos de luz reales sobre filamentos físicos'],
    VOXELS: ['03 / VOXELIZATION', 'FIXED OPTICAL VOXELS', 'One physical first-hit destination per fixed laser'],
    HEATMAP: ['06 / ANALYSIS', 'ACCESSIBILITY HEATMAP', 'Angular accessibility planned for phase 6'],
    FINAL_IMAGE: ['08 / RGB + SCATTERING', 'FINAL IMAGE', 'Spectral scattering planned for phase 8'],
  };
  const [index, title, description] = titles[mode];
  setText('#mode-index', index);
  setText('#mode-title', title);
  setText('#mode-description', description);
  element<HTMLElement>('#future-notice').hidden = !future;
  setText('#future-title', `${title} is not calculated in the current phase.`);
  element<HTMLElement>('#projection-toolbar').hidden = mode !== 'TARGET';
  setText('#stat-rays-label', mode === 'TARGET' ? 'Active rays' : 'Logical rays');
  if (snapshot) {
    updateStatistics(
      mode === 'TARGET' && displayedStatistics
        ? { ...snapshot, rays: displayedRays, statistics: displayedStatistics }
        : snapshot,
    );
  }
  element('.ray-legend').classList.toggle('hidden', mode === 'STRUCTURE' || mode === 'TARGET' || future);
  if (window.location.hash !== `#${mode}`) history.replaceState(null, '', `#${mode}`);
}

function animateProjection(timestamp: number): void {
  requestAnimationFrame(animateProjection);
  if (voxelTestRunning) {
    updateVoxelTest(timestamp);
    return;
  }
  const animationEnabled = element<HTMLInputElement>('#animate-projection').checked;
  if (
    !animationEnabled ||
    running ||
    currentMode !== 'TARGET' ||
    !snapshot ||
    !projectionSequence ||
    timestamp - lastProjectionFrameAt < PROJECTION_FRAME_INTERVAL_MS
  ) {
    return;
  }

  lastProjectionFrameAt = timestamp;
  const periodMs = Math.max(500, numberValue('#rotation-period') * 1000);
  const turnProgress = ((timestamp - projectionAnimationStartedAt) % periodMs) / periodMs;
  projectionFrameIndex = Math.floor(turnProgress * PROJECTION_FRAME_COUNT) % PROJECTION_FRAME_COUNT;
  const phase = turnProgress * Math.PI * 2;
  const direction = readRotationDirection();
  const rotation = new Vec3(
    direction.x * phase,
    direction.y * phase,
    direction.z * phase,
  );
  try {
    renderProjectionPose(rotation, readRotationPivot(), projectionFrameIndex);
  } catch (error) {
    console.error(error);
    element<HTMLInputElement>('#animate-projection').checked = false;
    updateProjectionAnimationControl();
    setText('#simulation-status', 'ANIMATION ERROR');
  }
}

for (const input of document.querySelectorAll<HTMLInputElement>('input[type="range"]')) {
  input.addEventListener('input', () => {
    updateOutputs();
    updateProjectionControlOutputs();
    if (input.id.startsWith('pivot-') && !element<HTMLInputElement>('#animate-projection').checked) {
      displayedPivot = readRotationPivot();
      renderProjectionPose(displayedRotation, displayedPivot, projectionFrameIndex);
    }
  });
}
for (const input of document.querySelectorAll<HTMLInputElement>('.toggles input')) {
  input.addEventListener('change', () => {
    if (input.id === 'occlusion') return;
    visibility = readVisibility();
    renderer.setVisibility(visibility);
  });
}
element<HTMLInputElement>('#show-projection-rays').addEventListener('change', () => {
  visibility = readVisibility();
  renderer.setVisibility(visibility);
  updateProjectionLaserControl();
});
element<HTMLInputElement>('#show-projection-mesh').addEventListener('change', () => {
  visibility = readVisibility();
  renderer.setVisibility(visibility);
  updateProjectionMeshControl();
});
element<HTMLInputElement>('#animate-projection').addEventListener('change', () => {
  lastProjectionFrameAt = performance.now();
  projectionAnimationStartedAt = lastProjectionFrameAt;
  updateProjectionAnimationControl();
  if (!element<HTMLInputElement>('#animate-projection').checked) {
    setText('#simulation-status', 'LASER SEQUENCE PAUSED');
  }
});

for (const select of document.querySelectorAll<HTMLSelectElement>('.rotation-axis-grid select')) {
  select.addEventListener('change', () => {
    projectionAnimationStartedAt = performance.now();
    if (!element<HTMLInputElement>('#animate-projection').checked) {
      displayedRotation = new Vec3();
      projectionFrameIndex = 0;
      renderProjectionPose(displayedRotation, readRotationPivot(), projectionFrameIndex);
    }
  });
}

for (const popover of document.querySelectorAll<HTMLDetailsElement>('.projection-popover')) {
  popover.addEventListener('toggle', () => {
    if (!popover.open) return;
    document.querySelectorAll<HTMLDetailsElement>('.projection-popover').forEach((other) => {
      if (other !== popover) other.open = false;
    });
  });
}

element('#reset-rotation').addEventListener('click', () => {
  element<HTMLSelectElement>('#rotation-x-direction').value = '0';
  element<HTMLSelectElement>('#rotation-y-direction').value = '1';
  element<HTMLSelectElement>('#rotation-z-direction').value = '0';
  element<HTMLInputElement>('#pivot-x').value = '0';
  element<HTMLInputElement>('#pivot-y').value = '0';
  element<HTMLInputElement>('#pivot-z').value = '0';
  element<HTMLInputElement>('#rotation-period').value = '8';
  updateProjectionControlOutputs();
  displayedRotation = new Vec3();
  displayedPivot = new Vec3();
  projectionFrameIndex = 0;
  projectionAnimationStartedAt = performance.now();
  renderProjectionPose(displayedRotation, displayedPivot, projectionFrameIndex);
});

element('#voxel-test-toggle').addEventListener('click', startVoxelTest);

element('#run-simulation').addEventListener('click', simulate);
element('#reset-camera').addEventListener('click', () => renderer.resetCamera());
document.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' && !(event.target instanceof HTMLInputElement)) simulate();
});

for (const button of document.querySelectorAll<HTMLButtonElement>('[data-mode]')) {
  button.addEventListener('click', () => {
    selectMode(button.dataset.mode as ViewMode, button.hasAttribute('data-future'));
  });
}

element<HTMLSelectElement>('#preset').addEventListener('change', (event) => {
  const name = (event.target as HTMLSelectElement).value;
  const preset = LAB_PRESETS[name];
  if (!preset) return;
  currentConfig = { ...currentConfig, ...preset };
  writeConfig(currentConfig);
  if (name === 'P0') {
    element<HTMLInputElement>('#animate-projection').checked = false;
    element<HTMLInputElement>('#show-projection-mesh').checked = true;
    visibility = readVisibility();
    renderer.setVisibility(visibility);
    updateProjectionAnimationControl();
    updateProjectionMeshControl();
  }
  simulate();
});

element<HTMLSelectElement>('#projection-figure').addEventListener('change', () => {
  selectMode('TARGET', false);
  simulate();
});

element<HTMLSelectElement>('#projection-precision').addEventListener('change', (event) => {
  element<HTMLSelectElement>('#laser-count').value = (event.target as HTMLSelectElement).value;
  selectMode('TARGET', false);
  simulate();
});

element<HTMLSelectElement>('#projection-mesh-pattern').addEventListener('change', (event) => {
  element<HTMLSelectElement>('#mesh-pattern').value = (event.target as HTMLSelectElement).value;
  selectMode('TARGET', false);
  simulate();
});

element<HTMLSelectElement>('#projection-laser-base').addEventListener('change', (event) => {
  const presetName = (event.target as HTMLSelectElement).value as keyof typeof LASER_BASE_PRESETS;
  const preset = LASER_BASE_PRESETS[presetName];
  if (!preset) return;
  element<HTMLInputElement>('#hemisphere-radius').value = String(preset.radius * 100);
  selectMode('TARGET', false);
  simulate();
});

element<HTMLSelectElement>('#laser-count').addEventListener('change', (event) => {
  element<HTMLSelectElement>('#projection-precision').value = (event.target as HTMLSelectElement).value;
});

element<HTMLSelectElement>('#mesh-pattern').addEventListener('change', (event) => {
  const select = element<HTMLSelectElement>('#projection-mesh-pattern');
  const value = (event.target as HTMLSelectElement).value;
  if (![...select.options].some(option => option.value === value)) select.add(new Option(value, value));
  select.value = value;
});
element('#toggle-lab').addEventListener('click', () => {
  const projectionOnly = element('.app-shell').classList.toggle('projection-only');
  element('#toggle-lab').textContent = projectionOnly ? 'Abrir laboratorio' : 'Cerrar laboratorio';
  element('#toggle-lab').setAttribute('aria-pressed', String(!projectionOnly));
  if (projectionOnly) selectMode('TARGET', false);
});
element('#apply-p0').addEventListener('click', () => {
  const preset = element<HTMLSelectElement>('#preset');
  preset.value = 'P0';
  preset.dispatchEvent(new Event('change'));
});
element('#projection-tolerance').addEventListener('change', () => { void simulate(); });
element('#quick-export').addEventListener('click', () => element('#export-config').click());
element('#quick-import').addEventListener('click', () => element('#import-config').click());
element('#export-measurements').addEventListener('click', () => {
  if (!snapshot || running) return;
  download('holo-net-measurements.json', JSON.stringify(measurementTemplate(snapshot), null, 2), 'application/json');
});
element('#import-measurements').addEventListener('click', () => {
  if (snapshot && !running) element('#measurements-file').click();
});
element<HTMLInputElement>('#measurements-file').addEventListener('change', async (event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file || !snapshot || running) return;
  const measuredSnapshot = snapshot;
  const request = simulationRequest;
  try {
    if (file.size > 10_000_000) throw new Error('Registro demasiado grande (máximo 10 MB).');
    const result = compareMeasurements(JSON.parse(await file.text()), measuredSnapshot);
    if (request !== simulationRequest) throw new Error('La simulación cambió durante la importación. Vuelve a cargar el registro.');
    setText('#measurement-summary', `${result.measured} medidos · ${result.failed} fallos · ${result.unmeasured} pendientes · RMS ${formatMillimetres(result.rms)} · máx. ${formatMillimetres(result.maximum)} frente al catálogo simulado`);
  } catch (error) {
    setText('#measurement-summary', error instanceof Error ? error.message : 'Mediciones inválidas');
  } finally { input.value = ''; }
});

element('#export-config').addEventListener('click', () => {
  let config: SimulationConfig;
  try { config = readConfig(); } catch (error) {
    setText('#public-status', error instanceof Error ? error.message : 'Configuración inválida');
    return;
  }
  download(
    'holo-net-config.json',
    JSON.stringify({ schemaVersion: 1, units: 'metres', ...config }, null, 2),
    'application/json',
  );
});

element('#import-config').addEventListener('click', () => element<HTMLInputElement>('#config-file').click());
element<HTMLInputElement>('#config-file').addEventListener('change', async (event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  try {
    if (file.size > 100_000) throw new Error('Configuración demasiado grande (máximo 100 KB).');
    const imported = parseConfig(JSON.parse(await file.text()));
    currentConfig = imported;
    writeConfig(currentConfig);
    simulate();
  } catch (error) {
    console.error(error);
    setText('#public-status', error instanceof Error ? error.message : 'Configuración inválida');
  } finally {
    (event.target as HTMLInputElement).value = '';
  }
});

element('#export-csv').addEventListener('click', () => {
  if (!snapshot) return;
  const exportedRays = currentMode === 'TARGET' ? displayedRays : snapshot.rays;
  const rows = [
    'ray_id,voxel_id,emitter_id,target_x_m,target_y_m,target_z_m,first_x_m,first_y_m,first_z_m,target_mesh,target_filament,first_mesh,first_filament,accessible,blocked,distance_m',
    ...exportedRays.map((ray) =>
      [
        ray.id,
        ray.voxelId,
        ray.emitterId,
        ray.target.x,
        ray.target.y,
        ray.target.z,
        ray.firstIntersection?.position.x ?? '',
        ray.firstIntersection?.position.y ?? '',
        ray.firstIntersection?.position.z ?? '',
        ray.targetMeshId,
        ray.targetFilamentId,
        ray.firstIntersection?.meshId ?? '',
        ray.firstIntersection?.filamentId ?? '',
        ray.targetAccessible,
        ray.blocked,
        ray.firstIntersection?.distance ?? '',
      ].join(','),
    ),
  ];
  download('holo-net-ray-results.csv', rows.join('\n'), 'text/csv');
});

writeConfig(currentConfig);
updateProjectionControlOutputs();
updateProjectionLaserControl();
updateProjectionMeshControl();
updateProjectionAnimationControl();
simulate();
requestAnimationFrame(animateProjection);
const requestedMode = (window.location.hash.slice(1) || 'TARGET') as ViewMode;
const initialButton = document.querySelector<HTMLButtonElement>(`[data-mode="${requestedMode}"]`);
if (initialButton) selectMode(requestedMode, initialButton.hasAttribute('data-future'));
